/**
 * v130 — CSV export for Range Log v2.
 *
 * Two export formats:
 *   1. Session-level CSV (legacy + v2 aggregate row)
 *   2. Shot-level CSV (one row per RichShot)
 *
 * Both are RFC-4180 quoted so commas/newlines in notes don't break parsing.
 */

import type { ShotSession } from './types';
import type { RichShot, SessionV2Extensions } from './v2-schema';
import { computeShotAggregates } from './aggregates';

type V2Session = ShotSession & SessionV2Extensions;

function csvCell(value: unknown): string {
  if (value === undefined || value === null) return '';
  const s = typeof value === 'string' ? value : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells: readonly unknown[]): string {
  return cells.map(csvCell).join(',');
}

/**
 * Session-level CSV — one row per session. Includes v2 aggregates when present.
 */
export function buildSessionsCsv(sessions: readonly ShotSession[]): string {
  const header = [
    'session_id',
    'logged_at',
    'distance_yd',
    'arrow_count',
    'group_size_in',
    'pattern',
    'notes',
    'rich_shot_count',
    'mean_radius_in',
    'group_95_radius_in',
    'center_x_in',
    'center_y_in',
    'da_ft',
    'wind_mph',
    'wind_dir_deg',
    'temperature_f',
    'altitude_ft',
    'location',
    'bow_name',
    'sighted_in_yd',
  ];
  const rows: string[] = [csvRow(header)];

  for (const session of sessions) {
    const v2 = session as V2Session;
    const aggs = v2.richShots ? computeShotAggregates(v2.richShots) : null;
    rows.push(
      csvRow([
        session.id,
        session.loggedAt,
        session.distanceYd,
        session.arrowCount,
        session.groupSizeIn,
        session.pattern,
        session.notes ?? '',
        aggs?.count ?? '',
        aggs?.meanRadiusIn?.toFixed(3) ?? '',
        aggs?.group95RadiusIn?.toFixed(3) ?? '',
        aggs?.centerX?.toFixed(3) ?? '',
        aggs?.centerY?.toFixed(3) ?? '',
        v2.conditions?.densityAltitudeFt ?? '',
        v2.conditions?.windMph ?? '',
        v2.conditions?.windDirDeg ?? '',
        v2.conditions?.temperatureF ?? '',
        v2.conditions?.altitudeFt ?? '',
        v2.location ?? '',
        v2.setupSnapshot?.setupName ?? '',
        v2.setupSnapshot?.sightedInYards ?? '',
      ]),
    );
  }
  return rows.join('\r\n') + '\r\n';
}

/**
 * Shot-level CSV — one row per RichShot, across all sessions provided.
 * Sessions without richShots contribute no rows.
 *
 * v132: when the session has v3 sets, each shot row is decorated with the
 * owning set's id / label / setupDelta. v1/v2 sessions just blank those columns.
 */
export function buildShotsCsv(sessions: readonly ShotSession[]): string {
  const header = [
    'session_id',
    'session_logged_at',
    'shot_id',
    'shot_at',
    'distance_yd',
    'target_type',
    'aim_point',
    'pin',
    'hit_x_in',
    'hit_y_in',
    'radial_offset_in',
    'wind_mph',
    'wind_dir_deg',
    'set_id',
    'set_label',
    'set_setup_delta',
    'notes',
  ];
  const rows: string[] = [csvRow(header)];

  for (const session of sessions) {
    const v2 = session as V2Session;
    if (!v2.richShots) continue;
    // Build a shot→set lookup if v3 sets are present
    const sets = (v2 as ShotSession & { sets?: import('./v3-schema').ShotSet[] }).sets;
    const shotToSet = new Map<string, { id: string; label?: string; setupDelta?: string }>();
    if (sets) {
      for (const set of sets) {
        for (const sid of set.shotIds) {
          shotToSet.set(sid, { id: set.id, label: set.label, setupDelta: set.setupDelta });
        }
      }
    }
    for (const shot of v2.richShots) {
      const r = Math.sqrt(shot.hitXIn ** 2 + shot.hitYIn ** 2);
      const setInfo = shotToSet.get(shot.id);
      rows.push(
        csvRow([
          session.id,
          session.loggedAt,
          shot.id,
          shot.shotAt,
          shot.distanceYd,
          shot.targetType,
          shot.aimPoint,
          shot.pin,
          shot.hitXIn.toFixed(3),
          shot.hitYIn.toFixed(3),
          r.toFixed(3),
          shot.windMph ?? '',
          shot.windDirDeg ?? '',
          setInfo?.id ?? '',
          setInfo?.label ?? '',
          setInfo?.setupDelta ?? '',
          shot.notes ?? '',
        ]),
      );
    }
  }
  return rows.join('\r\n') + '\r\n';
}

/**
 * Trigger a browser download of CSV content.
 * No-op outside a browser environment (SSR safe).
 */
export function downloadCsv(filename: string, content: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Helper: filename like "sparrow-sessions-2026-06-02.csv" */
export function isoToFilenameDate(iso = new Date().toISOString()): string {
  return iso.slice(0, 10);
}

/** Helper: pulls `RichShot[]` from a session, or empty. */
export function getRichShots(session: ShotSession): RichShot[] {
  return (session as V2Session).richShots ?? [];
}
